import type { Driver } from "driver.js";

/** The tour's stage keeping (the founder, 2026-10-07: "during that time it highlights a completely wrong area"): the highlight moves only to a
 *  place that has settled, and follows its place while the page moves under it. driver.js never holds a screen's element: each stop gets an
 *  invisible box (`ob-tour-span`) kept every frame on whatever element carries its marks right now (the inbox stop: around the views column
 *  and the queue), so a re-rendered or swapped element (a page that renders its content again) cannot leave the highlight behind.
 *  DOM reads only; the runner (`lib/tour.ts`) decides when. */

type Box = { left: number; top: number; right: number; bottom: number };
const rectKey = (r: Box) => `${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.right)},${Math.round(r.bottom)}`;
/** one animation frame, or 100 ms where the page gets none (a hidden tab) */
export const nextFrame = () => new Promise<void>((r) => { requestAnimationFrame(() => r()); setTimeout(r, 100); });

/** the element is on screen with a size (a views column folded away beside an open ticket does not count) */
function shown(el: Element): boolean {
  if (getComputedStyle(el).visibility === "hidden") return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && r.right > 0 && r.left < window.innerWidth;
}
/** the elements carrying the marks, every one shown, or null while any is missing */
export function markedElements(marks: readonly string[]): Element[] | null {
  const els = marks.map((m) => document.querySelector(`[data-tour="${m}"]`));
  return els.every((el): el is Element => !!el && shown(el)) ? els : null;
}
/** the smallest box around the elements */
export function unionBox(els: readonly Element[]): Box {
  const rs = els.map((el) => el.getBoundingClientRect());
  return { left: Math.min(...rs.map((r) => r.left)), top: Math.min(...rs.map((r) => r.top)), right: Math.max(...rs.map((r) => r.right)), bottom: Math.max(...rs.map((r) => r.bottom)) };
}
/** a CSS transition or animation still running on one of the elements or an ancestor (endless ones, like a spinner, do not count) */
function moving(els: readonly Element[]): boolean {
  return document.getAnimations().some((a) => {
    if (a.playState !== "running" || a.effect?.getTiming().iterations === Infinity) return false;
    const target = (a.effect as KeyframeEffect | null)?.target;
    return !!target && els.some((el) => target.contains(el));
  });
}

/** Waits until the stop's marks exist and are shown, their box has held still for two animation frames and no transition runs on them or
 *  their ancestors (the ticket page's columns slide for 150 ms when a ticket opens). False after `ms`. */
export async function settle(marks: readonly string[], ms = 8000): Promise<boolean> {
  const until = Date.now() + ms;
  let last = ""; let still = 0;
  while (Date.now() < until) {
    await nextFrame();
    const els = markedElements(marks);
    if (!els) { last = ""; still = 0; continue; }
    const key = rectKey(unionBox(els));
    still = key === last && !moving(els) ? still + 1 : 0;
    last = key;
    if (still >= 2) return true;
  }
  return false;
}

/** One invisible box over a stop's marks, placed on the elements carrying them every frame while the tour runs (looked up again each
 *  frame), so driver.js highlights one element that never goes stale. It carries `data-tour-stop` with the stop's id (never `data-tour`,
 *  which is the screens'). Where no marked element is shown it keeps its last place. */
export function spanOver(id: string, marks: readonly string[]): { el: HTMLElement; remove: () => void } {
  const el = document.createElement("div");
  el.className = "ob-tour-span"; el.dataset.tourStop = id; el.setAttribute("aria-hidden", "true");
  Object.assign(el.style, { left: "0px", top: "0px", width: "0px", height: "0px" });
  document.body.appendChild(el);
  let frame = 0;
  const place = () => {
    const els = marks.map((m) => document.querySelector(`[data-tour="${m}"]`)).filter((x): x is Element => !!x && shown(x));
    if (els.length) {
      const b = unionBox(els);
      Object.assign(el.style, { left: `${b.left}px`, top: `${b.top}px`, width: `${b.right - b.left}px`, height: `${b.bottom - b.top}px` });
    }
    frame = requestAnimationFrame(place);
  };
  place();
  return { el, remove: () => { cancelAnimationFrame(frame); el.remove(); } };
}

/** Keeps driver.js's highlight on its element while the page moves under it (driver.js itself only looks again on scroll and resize): every
 *  frame, when the element's box changed, `refresh()`. Not while `quiet()` (driver.js's own 500 ms glide), and not for an element that is
 *  gone or has no size (the highlight then stays where it was until the next stop is ready). Returns the stop. */
export function followStage(d: Driver, quiet: () => boolean): () => void {
  let last = ""; let frame = 0;
  const tick = () => {
    const el = d.getActiveElement();
    if (el?.isConnected && !quiet()) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        const key = rectKey(r);
        if (last && key !== last) d.refresh();
        last = key;
      }
    }
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
  const onResize = () => d.refresh();
  window.addEventListener("resize", onResize);
  return () => { cancelAnimationFrame(frame); window.removeEventListener("resize", onResize); };
}
